import { toQueryParams } from '@/helpers/ApiHelper'
import type { IPaginationOptions } from '@/misc/IPaginationOptions'
import type { IFirmwareRegistration } from '@/models/Firmware/IFirmwareRegistration'
import type { IFirmwareRegistrationCreate } from '@/models/Firmware/IFirmwareRegistrationCreate'
import type { IFirmwareRegistrationFilter } from '@/models/Firmware/IFirmwareRegistrationFilter'
import type { IFirmwareRegistrationUpdate } from '@/models/Firmware/IFirmwareRegistrationUpdate'
import type { IFirmwareStatus } from '@/models/Firmware/IFirmwareStatus'
import type { IFirmwareUpload } from '@/models/Firmware/IFirmwareUpload'
import type { IBlobSasResponse } from '@/models/IBlobSasResponse'
import type { IOperationResult } from '@/models/IOperationResult'
import type { IRestrictorMas } from '@/models/IRestrictorMas'
import type { IRetryOptions } from '@/models/IRetryOptions'
import type { IResultsPage } from '@/models/Pagination/IResultsPage'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IFirmwareService from './IFirmwareService'

@injectable()
export class FirmwareService implements IFirmwareService {
  private readonly apiClient: IApiClient
  private readonly firmwareUri: string = 'firmwares'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getFirmwareRegistrations(
    paginationOptions: IPaginationOptions,
    sortColum?: string,
    isAscending?: boolean,
    filter?: IFirmwareRegistrationFilter,
    restrictor?: IRestrictorMas,
    abortController?: AbortController
  ): Promise<IResultsPage<IFirmwareRegistration>> {
    let uri = `${this.firmwareUri}?pageSize=${paginationOptions.PageSize}&pageIndex=${paginationOptions.PageNumber}`
    uri += sortColum ? `&sortColumn=${sortColum}` : ''
    if (isAscending !== undefined && isAscending !== null) {
      uri += `&sortReversed=${!isAscending}`
    }
    const filters = filter ? toQueryParams<IFirmwareRegistrationFilter>(filter) : undefined
    uri += filters ? `&${filters}` : ''
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `&${restrictions}` : ''

    return this.apiClient.get<IResultsPage<IFirmwareRegistration>>(uri, abortController)
  }

  public async getFirmwareRegistration(id: string, restrictor?: IRestrictorMas, abortController?: AbortController, options?: IApiRequestOptions): Promise<IFirmwareRegistration> {
    let uri = `${this.firmwareUri}/${id}?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''

    return this.apiClient.get<IFirmwareRegistration>(uri, abortController, options)
  }

  public async downloadFirmware(id: string, restrictor?: IRestrictorMas): Promise<{ blob: Blob; fileName?: string }> {
    let uri = `${this.firmwareUri}/${id}/file?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''
    return this.apiClient.getBlob(uri)
  }

  public async downloadFirmwareRedirect(id: string, restrictor?: IRestrictorMas): Promise<IBlobSasResponse> {
    let uri = `${this.firmwareUri}/${id}/file?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''

    return await this.apiClient.get<IBlobSasResponse>(uri)
  }

  public async updateFirmwareRegistration(id: string, firmwareRegistrationUpdate: IFirmwareRegistrationUpdate, restrictor?: IRestrictorMas): Promise<IOperationResult> {
    let uri = `${this.firmwareUri}/${id}?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''
    return this.apiClient.put<IOperationResult>(uri, firmwareRegistrationUpdate)
  }

  public async uploadFirmware(id: string, file: File, restrictor?: IRestrictorMas): Promise<IOperationResult> {
    let uri = `${this.firmwareUri}/${id}/file?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''
    const formData = new FormData()
    formData.append('file', file, file.name)
    return this.apiClient.postFormData<IOperationResult>(uri, formData)
  }

  public async createFirmwareRegistration(createFirmwareRegistration: IFirmwareRegistrationCreate, restrictor?: IRestrictorMas): Promise<IOperationResult> {
    let uri = `${this.firmwareUri}?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''
    return this.apiClient.post<IOperationResult>(uri, createFirmwareRegistration)
  }

  public async getFirmwareStatuses(): Promise<IFirmwareStatus[]> {
    return this.apiClient.get('firmware-statuses')
  }

  public async updateChargePointFirmware(firmwareId: string, chargePointId: string, retryOptions?: IRetryOptions, restrictor?: IRestrictorMas): Promise<IOperationResult> {
    let uri = `${this.firmwareUri}/${firmwareId}/chargepoint/${chargePointId}/update?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''

    return this.apiClient.put<IOperationResult>(uri, retryOptions ?? {})
  }

  public async getFirmwareUploadUrl(firmwareUpload: IFirmwareUpload): Promise<IBlobSasResponse> {
    const uri = `${this.firmwareUri}/url`
    return this.apiClient.post<IBlobSasResponse>(uri, firmwareUpload)
  }

  public async completeFirmwareUpload(firmwareUpload: IFirmwareUpload, restrictor?: IRestrictorMas): Promise<IOperationResult> {
    let uri = `${this.firmwareUri}/uploaded?`
    const restrictions = restrictor ? toQueryParams<IRestrictorMas>(restrictor) : undefined
    uri += restrictions ? `${restrictions}` : ''

    return this.apiClient.post(uri, firmwareUpload)
  }

  public async uploadFirmwareBlob(uploadUrl: string, file: File): Promise<IOperationResult> {
    try {
      await this.apiClient.putBlob(uploadUrl, file)
      return { Success: true }
    } catch {
      return { Success: false }
    }
  }
}
